/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { MultiSelect } from '@eliadmin/core/src/components/ui/multi-select';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React, { useMemo } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import type { InputProps } from 'react-select';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';

type FieldProps<TFormValues extends FieldValues> = {
  control: Control<TFormValues>;
  name: FieldPath<TFormValues>;
  placeholder?: string;
  label?: string | React.ReactNode;
  required?: boolean;
  type?: InputProps['type'] | 'textarea';
  options?: { value: string | number; label: string }[];
  description?: string | React.ReactNode;
  isDisabled?: boolean;
  bottomBorder?: boolean;
  className?: {
    wrapper?: string;
  };
  menuPlacement?: 'top' | 'bottom';
  before?: React.ReactNode;
  after?: React.ReactNode;
};

export function Field<TFormValues extends FieldValues>(props: FieldProps<TFormValues>) {
  const { t } = useTranslations();
  const content = useMemo(() => {
    if (props.type === 'select') {
      if (!props.options) {
        throw new Error('Select type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem
                required={props.required}
                className={cn('w-full', props.className?.wrapper)}
              >
                <FormLabel className="m-0 p-0 text-xs font-medium text-[#111827] sm:text-sm">
                  {props.label}
                </FormLabel>
                <Select
                  value={field.value != null ? String(field.value) : undefined}
                  onValueChange={field.onChange}
                  disabled={props.isDisabled}
                >
                  <FormControl>
                    <SelectTrigger
                      innerTriggerClassName="px-3"
                      className={cn(
                        'relative h-[52px] rounded-[20px] border border-border bg-white px-0 text-black',
                        'ea-field__select-trigger',
                      )}
                    >
                      <SelectValue
                        placeholder={props.placeholder ?? t('text1410')}
                        className="pt-3 text-sm text-black sm:text-base"
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent
                    sideOffset={3}
                    className="hide-scroll-bar max-h-[160px] overflow-y-auto rounded-[20px] shadow-none"
                  >
                    {props.options?.map(o => (
                      <SelectItem
                        key={o.value}
                        value={String(o.value)}
                        className="rounded-[16px]"
                      >
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {props.description && (
                  <p className="leadng-[130%] mt-1 text-[10px] leading-[130%] text-[#6B7280] sm:text-[12px]">
                    {props.description}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'multiselect') {
      if (!props.options) {
        throw new Error('MultiSelect type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem className="relative min-h-[56px]">
                <FormControl>
                  <MultiSelect
                    options={props.options} // Options passed in
                    isMulti={true} // Multi select enabled
                    placeholder={props.placeholder} // Placeholder text
                    value={field.value} // Controlled field value
                    onChange={field.onChange} // Handling changes
                    isDisabled={props.isDisabled} // Disabled state
                    noOptionsMessage={() => t('text477')} // Custom message for no options
                    stylesConfig={{}} // Custom styles if needed (optional)
                    menuPlacement={props.menuPlacement || 'bottom'}
                    classNamesConfig={{
                      valueContainer() {
                        return cn('pl-2  bg-input');
                      },
                      control() {
                        return cn('!min-h-[56px] bg-input rounded-lg');
                      },
                      menu() {
                        return cn('!z-[100] !bg-input');
                      },
                      multiValue() {
                        return cn('bg-primary text-primary-foreground');
                      },
                      menuList() {
                        return cn('!z-[100] !bg-input block');
                      },
                    }} // Custom classNames if needed (optional)
                    variant={'outlined'}
                  />
                </FormControl>
                {props.description && (
                  <p className="leadng-[130%] mt-1 text-[10px] leading-[130%] text-[#6B7280] sm:text-[12px]">
                    {props.description}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'radio') {
      if (!props.options) {
        throw new Error('Radio type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem required={props.required} className="flex flex-col gap-2 p-[1px]">
                {props.label && <FormLabel>{props.label}</FormLabel>}
                <FormControl>
                  <RadioGroup
                    value={String(field.value)}
                    onValueChange={field.onChange}
                    disabled={props.isDisabled}
                    className="flex flex-row gap-4 pl-2"
                  >
                    {props.options?.map(o => (
                      <div key={o.value} className="flex gap-2">
                        <RadioGroupItem id={`radiogroupitem-${o.value}`} value={String(o.value)} />
                        <Label className="flex items-center" htmlFor={`radiogroupitem-${o.value}`}>
                          <p className="text-sm font-semibold text-muted">{o.label}</p>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                {props.description && (
                  <p className="leadng-[130%] mt-1 text-[10px] leading-[130%] text-[#6B7280] sm:text-[12px]">
                    {props.description}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'checkbox') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              <div className="flex w-full flex-row items-center gap-2">
                <FormControl>
                  <Checkbox
                    {...(field as any)}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={props.isDisabled}
                    aria-disabled={props.isDisabled}
                  />
                </FormControl>
                {props.label && <FormLabel>{props.label}</FormLabel>}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'tel') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              {props.label && (
                <FormLabel className="m-0 p-0 text-xs font-medium text-[#111827] sm:text-sm">
                  {props.label}
                </FormLabel>
              )}
              <FormControl>
                <PhoneInputField
                  placeholder={props.placeholder}
                  type={props.type}
                  {...(field as any)}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                />
              </FormControl>
              {props.description && (
                <p className="leadng-[130%] mt-1 text-[10px] leading-[130%] text-[#6B7280] sm:text-[12px]">
                  {props.description}
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'textarea') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem
              required={props.required}
              className={cn(
                'flex w-full flex-col items-start justify-start gap-1',
                props?.className?.wrapper,
              )}
            >
              {props.label && (
                <FormLabel className="m-0 p-0 text-xs font-medium text-[#111827] sm:text-sm">
                  {props.label}
                </FormLabel>
              )}
              <FormControl>
                <Textarea
                  placeholder={props.placeholder}
                  {...(field as any)}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                  rounded={'default'}
                />
              </FormControl>
              {props.description && (
                <p className="leadng-[130%] mt-1 text-[10px] leading-[130%] text-[#6B7280] sm:text-[12px]">
                  {props.description}
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={props.control}
        name={props.name}
        render={({ field }) => (
          <FormItem
            required={props.required}
            className={cn(
              'flex w-full flex-col items-start justify-start gap-1',
              props?.className?.wrapper,
            )}
          >
            {props.label && (
              <FormLabel className="m-0 p-0 text-xs font-medium text-[#111827] sm:text-sm">
                {props.label}
              </FormLabel>
            )}
            <FormControl>
              <Input
                placeholder={props.placeholder}
                type={props.type}
                {...(field as any)}
                disabled={props.isDisabled}
                aria-disabled={props.isDisabled}
                variant={'transparent'}
                rounded={'default'}
                before={props.before}
                after={props.after}
              />
            </FormControl>
            {props.description && (
              <Paragraph size="xs" color="neutral">
                {props.description}
              </Paragraph>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }, [
    props.control,
    props.isDisabled,
    props.required,
    props.label,
    props.name,
    props.options,
    props.placeholder,
    props.type,
  ]);

  return <div className={cn(props.className?.wrapper)}>{content}</div>;
}
